import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

export default function GiftCardManagementList() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const fetchGiftCards = async (opts = {}) => {
    const nextPage = opts.page ?? page;
    const nextLimit = opts.limit ?? limit;

    setLoading(true);
    setErr("");

    try {
      const res = await api.get("/admin/gift-card-management", {
        params: {
          search: search || undefined,
          status: status !== "" ? status : undefined,
          page: nextPage,
          limit: nextLimit,
          sortBy: "created_at",
          order: "DESC",
        },
      });

      setRows(res.data?.data || []);
      setMeta(res.data?.meta || { total: 0, page: nextPage, limit: nextLimit, totalPages: 1 });
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to load gift card management";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGiftCards({ page: 1 });
  }, []);

  const onApply = (e) => {
    e.preventDefault();
    setPage(1);
    fetchGiftCards({ page: 1 });
  };

  const openCreate = () => {
    navigate("/admin/gift-card-management/create");
  };

  const openEdit = (row) => {
    navigate(`/admin/gift-card-management/edit/${row.id}`);
  };

  const permanentDelete = async (id) => {
      const ok = window.confirm("Permanent delete? This cannot be undone.");
      if (!ok) return;

      try {
        await api.delete(`/admin/gift-card-management/${id}/permanent`);
        toast.success("Deleted permanently");
        await fetchGiftCards();
      } catch (e) {
        toast.error(e?.response?.data?.message || e.message || "Permanent delete failed");
      }
  };

  const deactivate = async (id) => {
    const ok = window.confirm("Deactivate this gift card management?");
    if (!ok) return;

    try {
      await api.delete(`/admin/gift-card-management/${id}`);
      toast.success("Gift card management deactivated");
      await fetchGiftCards();
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Deactivate failed");
    }
  };

  const goPrev = () => {
    if (meta.page <= 1) return;
    const p = meta.page - 1;
    setPage(p);
    fetchGiftCards({ page: p });
  };

  const goNext = () => {
    if (meta.page >= meta.totalPages) return;
    const p = meta.page + 1;
    setPage(p);
    fetchGiftCards({ page: p });
  };

  const onLimitChange = (e) => {
    const v = Number(e.target.value) || 10;
    setLimit(v);
    setPage(1);
    fetchGiftCards({ page: 1, limit: v });
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return "-";
    return `₹${Number(price).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDiscount = (giftCard) => {
    if (!giftCard.discount_type || !giftCard.discount_value) return "-";
    if (giftCard.discount_type === "PERCENT") {
      return `${giftCard.discount_value}%`;
    }
    return `₹${Number(giftCard.discount_value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPriceRange = (giftCard) => {
    if (giftCard.price_type === "single" && giftCard.single_price) {
      return formatPrice(giftCard.single_price);
    }
    return `${formatPrice(giftCard.min_price)} - ${formatPrice(giftCard.max_price)}`;
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Gift Card Management</h1>
          <p className="text-sm text-gray-600 mt-1">Manage gift card configurations and pricing ranges</p>
        </div>

        <button
          className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90"
          onClick={openCreate}
        >
          + Create Gift Card
        </button>
      </div>

      <form onSubmit={onApply} className="mt-6 flex flex-wrap items-end gap-3 border rounded-2xl p-4">
        <div className="w-full sm:w-auto sm:min-w-[240px]">
          <label className="text-sm font-semibold">Search</label>
          <input
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            placeholder="Search by title, or description..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="w-full sm:w-auto sm:min-w-[200px]">
          <label className="text-sm font-semibold">Status</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>

        <div className="w-full sm:w-auto sm:min-w-[140px]">
          <label className="text-sm font-semibold">Per page</label>
          <select
            className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
            value={limit}
            onChange={onLimitChange}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>

        <button
          type="submit"
          className="w-full sm:w-auto rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90"
        >
          Apply
        </button>
      </form>

      {err ? <div className="mt-4 text-sm text-red-600">{err}</div> : null}

      <div className="mt-6 border rounded-2xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <span>Gift Card Management List</span>

          <div className="flex flex-wrap items-center gap-2 text-sm">
            <button
              onClick={goPrev}
              disabled={meta.page <= 1 || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
              type="button"
            >
              Prev
            </button>

            <span className="text-gray-700">
              Page <b>{meta.page}</b> / <b>{meta.totalPages}</b> (Total: <b>{meta.total}</b>)
            </span>

            <button
              onClick={goNext}
              disabled={meta.page >= meta.totalPages || loading}
              className="rounded-lg border px-3 py-1 font-semibold disabled:opacity-50"
              type="button"
            >
              Next
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-gray-600">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">No gift card management found.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-t bg-white">
              <tr className="text-left">
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Price Type</th>
                <th className="px-4 py-3">Price Range</th>
                <th className="px-4 py-3">Discount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>

            <tbody className="border-t">
              {rows.map((giftCard) => (
                <tr key={giftCard.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">{giftCard.title}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-700">
                      {giftCard.price_type === "single" ? "Single" : "Range"}
                    </span>
                  </td>
                  <td className="px-4 py-3">{formatPriceRange(giftCard)}</td>
                  <td className="px-4 py-3">{formatDiscount(giftCard)}</td>

                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
                        Number(giftCard.status) === 1
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {Number(giftCard.status) === 1 ? "Active" : "Inactive"}
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                        onClick={() => openEdit(giftCard)}
                      >
                        Edit
                      </button>

                      {Number(giftCard.status) === 1 ? (
                        <button
                          type="button"
                          className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                          onClick={() => deactivate(giftCard.id)}
                        >
                          Deactivate
                        </button>
                      ) : null}

                        <button
                        type="button"
                        className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                        onClick={() => permanentDelete(giftCard.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
